import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("p", null, "Your file has been uploaded, but we haven't created your online tree yet.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, "Do you want to discard the uploaded file?", -1)

import { computed, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { usePageTitle } from '@webapp/util/AppUtil'
import { useConfirmation } from '@webapp/util/Confirmation'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { useSyncSessionStore } from './SyncSessionStore'
import { SourceState } from './SyncSessionModel'
import { useSyncStore } from '@webapp/rd/SyncStore'
import { Sync, SyncState } from '@webapp/rd/ResearchDataModel'
import { LoadMode } from '@webapp/util/AsyncData'
import { useFeatureStore, SyncChangesFeature } from '@/rd/FeatureStore'
import Modal from '@webapp/util/Modal.vue'
import SyncMethodPage from './SyncMethodPage.vue'
import SyncFilePage from './SyncFilePage.vue'
import SyncOptionsPage from './SyncOptionsPage.vue'
import SyncOperationPage from './SyncOperationPage.vue'

type Step = 'method' | 'file' | 'options' | 'operation'


export default /*#__PURE__*/_defineComponent({
  __name: 'SyncSessionPage',
  setup(__props) {

const router = useRouter()
const tokenManager = useTokenManager()
const syncSessionStore = useSyncSessionStore()
const syncStore = useSyncStore()
const featureStore = useFeatureStore()

const stepRef = ref<Step>('method')
const currentSessionIdRef = ref<string>()
const abandonModalRef = ref<InstanceType<typeof Modal>>()
const confirmAbandonFile = useConfirmation(abandonModalRef)

const syncChangesEnabledRef = computed(() => featureStore.isFeatureEnabled(SyncChangesFeature))
watch(syncChangesEnabledRef, (enabled) => {
  if (enabled === false) {
    router.replace('/syncapp')
  }
}, { immediate: true })

usePageTitle('Upload my tree')

function storageKey() {
  return `tc.users.${tokenManager.current.userId ?? ''}.activeSyncSessionId`
}

// Persist the current upload so a reload can resume it.
watch(currentSessionIdRef, (id) => {
  if (id) {
    localStorage.setItem(storageKey(), id)
  }
  else {
    localStorage.removeItem(storageKey())
  }
})

function onUploaded(sessionId: string) {
  currentSessionIdRef.value = sessionId
  stepRef.value = 'options'
}

function onStarted() {
  stepRef.value = 'operation'
}

function onFailed() {
  stepRef.value = 'options'
}

function onComplete() {
  currentSessionIdRef.value = undefined
}

async function onOptionsBack() {
  if (!(await confirmAbandonFile.ask())) {
    return
  }
  await discardCurrent()
  stepRef.value = 'file'
}

// The server GC'd the session mid-flow -- drop it without a confirm and let the user re-upload.
async function onUnusable() {
  await discardCurrent()
  stepRef.value = 'file'
}

async function discardCurrent() {
  await syncSessionStore.abandonSessionAsync(currentSessionIdRef.value)
  currentSessionIdRef.value = undefined
}

onBeforeRouteLeave(async () => {
  // Leaving the wizard with an uploaded-but-not-synced file discards it, after confirming.
  const id = currentSessionIdRef.value
  if (stepRef.value !== 'options' || !id || syncSessionStore.getState(id)?.syncId) {
    return true
  }
  const confirmed = await confirmAbandonFile.ask()
  if (confirmed) {
    await syncSessionStore.abandonSessionAsync(id)
    localStorage.removeItem(storageKey())
    currentSessionIdRef.value = undefined
  }
  return confirmed
})

onMounted(async () => {
  const storedId = localStorage.getItem(storageKey())
  if (!storedId) {
    return
  }
  const session = await syncSessionStore.ensureSessionLoadedAsync(storedId)
  if (!session || session.sourceState !== SourceState.Ready) {
    localStorage.removeItem(storageKey())
    return
  }
  currentSessionIdRef.value = storedId
  if (!session.workspaceId) {
    stepRef.value = 'options'
    return
  }
  // Already submitted: rejoin the sync if it's still running, offer retry if it failed, else forget.
  const syncId = await syncSessionStore.resumeSyncAsync(storedId)
  const sync = syncId ? syncStore.getAsyncSync(syncId, LoadMode.TrackOnly)?.data : undefined
  if (sync && !Sync.isFinalState(sync.state)) {
    stepRef.value = 'operation'
  }
  else if (sync && (sync.state === SyncState.Failed || sync.state === SyncState.Cancelled)) {
    stepRef.value = 'options'
  }
  else {
    currentSessionIdRef.value = undefined
  }
})

return (_ctx: any,_cache: any) => {
  return (syncChangesEnabledRef.value)
    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
        (stepRef.value === 'method')
          ? (_openBlock(), _createBlock(SyncMethodPage, {
              key: 0,
              onChooseFile: _cache[0] || (_cache[0] = ($event: any) => (stepRef.value = 'file'))
            }))
          : (stepRef.value === 'file')
            ? (_openBlock(), _createBlock(SyncFilePage, {
                key: 1,
                onUploaded: onUploaded,
                onBack: _cache[1] || (_cache[1] = ($event: any) => (stepRef.value = 'method'))
              }))
            : (stepRef.value === 'options')
              ? (_openBlock(), _createBlock(SyncOptionsPage, {
                  key: 2,
                  "session-id": currentSessionIdRef.value!,
                  onStarted: onStarted,
                  onBack: onOptionsBack,
                  onUnusable: onUnusable
                }, null, 8, ["session-id"]))
              : (stepRef.value === 'operation')
                ? (_openBlock(), _createBlock(SyncOperationPage, {
                    key: 3,
                    "session-id": currentSessionIdRef.value,
                    onComplete: onComplete,
                    onFailed: onFailed
                  }, null, 8, ["session-id"]))
                : _createCommentVNode("", true),
        _createVNode(Modal, {
          ref_key: "abandonModalRef",
          ref: abandonModalRef,
          onOk: _unref(confirmAbandonFile).confirm,
          onHidden: _unref(confirmAbandonFile).dismiss
        }, {
          title: _withCtx(() => [
            _createTextVNode("Discard uploaded file?")
          ]),
          okText: _withCtx(() => [
            _createTextVNode("Discard the file")
          ]),
          default: _withCtx(() => [
            _hoisted_1,
            _hoisted_2
          ]),
          _: 1
        }, 8, ["onOk", "onHidden"])
      ], 64))
    : _createCommentVNode("", true)
}
}

})